import { useMemo, useEffect } from 'react';
import { Select } from '@/shared/components/ui/Select';
import { Input } from '@/shared/components/ui/Input';
import { TemplatePreview } from '@/modules/templates/components/TemplatePreview';
import { renderTemplateSeguro } from '@/modules/templates/components/templateRender';
import { useTemplatesDropdown } from '@/modules/templates/hooks/useTemplatesDropdown';
import { useTemplateDropdownDetail } from '@/modules/templates/hooks/useTemplateDropdownDetail';

export interface TemplateSelectValue {
  templateId: string;
  variables: Record<string, string>;
}

interface Props {
  channelId: string;
  value: TemplateSelectValue;
  onChange: (v: TemplateSelectValue) => void;
  disabled?: boolean;
  onValidityChange?: (valid: boolean) => void;
}

export function TemplateSelectWithPreview({ channelId, value, onChange, disabled, onValidityChange }: Props) {
  const { data: templates, isLoading } = useTemplatesDropdown(channelId);

  // useTemplatesDropdown already returns only approved templates per the backend,
  // but we filter client-side as well for safety.
  const approved = useMemo(
    () => (templates ?? []).filter((t) => t.status === 'approved'),
    [templates],
  );

  const { data: detail } = useTemplateDropdownDetail(value.templateId || undefined);

  /**
   * O que o usuário digitou, direto para o renderizador único — sem estado
   * intermediário. Era daí que vinha o balão congelado: o valor novo chegava
   * como `example` e o `testValues` interno do preview nunca o substituía.
   * Posição vazia aparece como `[Rótulo]`, não como `{{1}}`.
   */
  const render = useMemo(
    () =>
      detail
        ? renderTemplateSeguro(detail.providerConfig, value.variables, detail.variables)
        : null,
    [detail, value.variables],
  );

  /**
   * When the selected template changes, pre-fill variables from each variable's `example`.
   * Existing user-typed values for the same position are preserved.
   */
  useEffect(() => {
    if (!detail) return;
    const next: Record<string, string> = {};
    (detail.variables ?? []).forEach((v) => {
      next[String(v.position)] = value.variables[String(v.position)] ?? v.example ?? '';
    });
    onChange({ templateId: value.templateId, variables: next });
    // Only run when the template identity changes, not on every variables edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  /**
   * Report validity to parent whenever templateId, variable values, or detail changes.
   * Valid = template selected AND all required variables have a non-empty value.
   * Treats detail not yet loaded (or no template selected) as invalid.
   */
  useEffect(() => {
    if (!onValidityChange) return;
    if (!value.templateId || !detail) {
      onValidityChange(false);
      return;
    }
    const allRequiredFilled = (detail.variables ?? [])
      .filter((v) => v.required)
      .every((v) => (value.variables[String(v.position)] ?? '').trim() !== '');
    onValidityChange(allRequiredFilled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.templateId, value.variables, detail?.id, onValidityChange]);

  return (
    <div className="space-y-3">
      {/* Template selector */}
      <Select
        value={value.templateId}
        onChange={(id) => onChange({ templateId: id, variables: {} })}
        placeholder="Selecione um template"
        options={approved.map((t) => ({ value: t.id, label: t.name }))}
        disabled={disabled ?? isLoading}
        data-testid="template-select"
      />

      {/* Editable variable inputs */}
      {detail && (detail.variables?.length ?? 0) > 0 && (
        <div className="space-y-2" data-testid="template-variables">
          {detail.variables.map((v) => (
            <div key={v.position}>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: 'var(--theme-text)' }}
              >
                {`{{${v.position}}}`} &mdash; {v.label}
                {v.required && (
                  <span className="ml-1" style={{ color: 'var(--theme-error-color)' }}>
                    *
                  </span>
                )}
              </label>
              <Input
                type="text"
                value={value.variables[String(v.position)] ?? ''}
                onChange={(e) =>
                  onChange({
                    templateId: value.templateId,
                    variables: {
                      ...value.variables,
                      [String(v.position)]: e.target.value,
                    },
                  })
                }
                placeholder={v.example}
                data-testid={`template-var-${v.position}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Live preview */}
      {detail && (
        <div data-testid="template-preview">
          <TemplatePreview
            render={render}
            providerType={detail.providerConfig?.providerType}
            hideHeader={true}
            fullWidthBubble={true}
          />
        </div>
      )}
    </div>
  );
}
